import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, Navigate, useLocation } from "react-router-dom";
import { User, Package, MapPin, Lock, LogOut, Heart } from "lucide-react";
import { useCustomerAuth } from "@/contexts/CustomerAuthContext";
import SEO from "@/components/storefront/SEO";

const ITEMS = [
  { to: "/account", label: "Overview", icon: User, end: true, testId: "acct-nav-overview" },
  { to: "/account/orders", label: "Orders", icon: Package, testId: "acct-nav-orders" },
  { to: "/account/addresses", label: "Addresses", icon: MapPin, testId: "acct-nav-addresses" },
  { to: "/account/security", label: "Security", icon: Lock, testId: "acct-nav-security" },
  { to: "/wishlist", label: "Wishlist", icon: Heart, testId: "acct-nav-wishlist" },
];

export default function AccountLayout() {
  const { customer, loading, isAuthed, signOut } = useCustomerAuth();
  const loc = useLocation();

  if (loading) {
    return <div className="mx-auto max-w-6xl px-6 py-20"><div className="h-[400px] animate-pulse rounded-md bg-[#F1E3DC]" /></div>;
  }

  if (!isAuthed) {
    const redirect = loc.pathname + loc.search;
    return <Navigate to={`/account/login?redirect=${encodeURIComponent(redirect)}`} replace />;
  }

  return (
    <div className="mx-auto max-w-6xl px-6 py-12 md:py-16" data-testid="account-layout">
      <SEO title="My account" noindex />
      <div className="mb-10 text-xs uppercase tracking-[0.25em] text-[#6B5158]">
        <Link to="/" className="hover:text-[#2B2127]">Home</Link>
        <span className="mx-2">/</span>
        <span>Account</span>
      </div>

      <div className="mb-12 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#6B5158]">Hi {customer.name?.split(" ")[0] || "there"},</div>
          <h1 className="mt-2 font-display text-5xl leading-tight md:text-[72px]">My <span className="font-display-soft">account</span></h1>
        </div>
        <button onClick={signOut} className="inline-flex items-center gap-1.5 rounded-full border border-[#E6D4CC] bg-white px-4 py-2 text-xs uppercase tracking-[0.15em] text-[#2B2127] transition-colors hover:bg-[#F1E3DC]" data-testid="acct-signout-btn">
          <LogOut size={12} /> Sign out
        </button>
      </div>

      <div className="grid grid-cols-1 gap-10 md:grid-cols-[220px_1fr]">
        <aside className="md:sticky md:top-24 md:h-fit">
          <nav className="space-y-1" data-testid="acct-sidebar">
            {ITEMS.map((it) => {
              const Icon = it.icon;
              return (
                <NavLink
                  key={it.to}
                  to={it.to}
                  end={it.end}
                  data-testid={it.testId}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 rounded-md px-3 py-2.5 text-sm transition-colors ${
                      isActive ? "bg-[#F1E3DC] font-medium text-[#2B2127]" : "text-[#6B5158] hover:bg-[#FAF6F1] hover:text-[#2B2127]"
                    }`
                  }
                >
                  <Icon size={15} strokeWidth={1.4} />
                  {it.label}
                </NavLink>
              );
            })}
          </nav>
        </aside>

        <div className="min-w-0">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
